import React, {ChangeEvent, useState} from 'react';
import {FieldProp} from '../../../types';
import {NestedForm} from '../NestedForm';
import {Modal} from '../../Modal';

import SearchForm from './SearchForm';
import InvoicesResultList from './InvoicesResultList';
import LinesResultList from './LinesResultList';
// @ts-ignore
import ActivitySpinner from './spinner.svg';

import {SEARCH_TYPE_INVOICE, SEARCH_TYPE_ORDER, UOM_MAP} from './constants';
import {Etl, EtlResponse} from '../../../_services/Etl';

type InvoiceSearchProps = FieldProp & {};

export const InvoiceSearch: React.FunctionComponent<InvoiceSearchProps> = ({
                                                                               value,
                                                                               onChange,
                                                                               ...rest
                                                                           }): JSX.Element => {
    const [showModal, setShowModal] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isWorking, setIsWorking] = useState<boolean>(false);
    const [searchType, setSearchType] = useState<string>('');
    const [searchResults, setSearchResults] = useState<any>([]);
    const [selectedLines, setSelectedLines] = useState({});
    const [currentInvoiceNumber, setCurrentInvoiceNumber] = useState<string>('');
    const [currentOrderNumber, setCurrentOrderNumber] = useState<string>('');
    const [currentPurchaseOrderNumber, setCurrentPurchaseOrderNumber] = useState<string>('');
    const [globalDiscount, setGlobalDiscount] = useState<string>('');

    const handleSearch = (searchType: string, query: string): void => {
        if (searchType === SEARCH_TYPE_ORDER) {
            orderSearch(query);
        } else if (searchType === SEARCH_TYPE_INVOICE) {
            invoiceSearch(query);
        }
    };

    const orderSearch = (orderNumber: string): void => {
        setIsWorking(true);
        setSearchType(SEARCH_TYPE_ORDER);
        setErrorMessage('');
        setSearchResults([]);

        Etl.getInstance().process('fletch-form-19-invoices', {orderNumber})
            .then((response: EtlResponse) => {
                if (response.ok) {
                    setSearchResults(response.json.result.invoiceNumbers);
                } else {
                    setErrorMessage(response.json.message || 'Error performing search');
                }
                setIsWorking(false);
            })
            .catch((error: any) => {
                setIsWorking(false);
                setErrorMessage(error);
            });
    };

    const invoiceSearch = (invoiceNumber: string, startingFrom: string = ''): void => {
        console.log('invoiceSearch ', invoiceNumber, startingFrom);
        setIsWorking(true);
        setSearchType(SEARCH_TYPE_INVOICE);
        setErrorMessage('');
        setCurrentInvoiceNumber('');
        setCurrentOrderNumber('');
        setCurrentPurchaseOrderNumber('');

        if (!startingFrom) {
            setSearchResults([]);
            setSelectedLines({});
        }

        Etl.getInstance().process('fletch-form-19-lines', {invoiceNumber, startingFrom})
            .then((response: EtlResponse) => {
                if (response.ok) {
                    console.log(response.json.result.lines.length);
                    setSearchResults((state: any) => {
                        return [
                            ...state,
                            ...(response.json.result.lines.map((line: any) => ({
                                ...line,
                                discount: 1
                            })))
                        ]
                    });
                    setCurrentInvoiceNumber(invoiceNumber);
                    setCurrentOrderNumber(response.json.result.orderNumber);
                    setCurrentPurchaseOrderNumber(response.json.result.purchaseOrderNumber);

                    if (response.json.result.nextKey) {
                        invoiceSearch(invoiceNumber, response.json.result.nextKey);
                    }

                    setIsWorking(false);
                } else {
                    setErrorMessage(response.json.message || 'Error performing search');
                    setIsWorking(false);
                }
            })
            .catch((error: any) => {
                setIsWorking(false);
                setErrorMessage(error);
            });
    };

    const handleInvoiceSelect = (invoiceNumber: string) => {
        invoiceSearch(invoiceNumber);
    };

    const handleNewSearch = () => {
        setErrorMessage('');
        setIsWorking(false);
        setSearchType('');
        setSearchResults([]);
        setSelectedLines({});
        setCurrentInvoiceNumber('');
        setCurrentOrderNumber('');
        setCurrentPurchaseOrderNumber('');
    };

    const handleSelectedLines = (selectedLines: any) => {
        setSelectedLines({...selectedLines});
    };

    const handleLineChange = (item: any) => {
        const newSearchResults = searchResults.map((searchResult: any) => {
            return {
                ...searchResult,
                quantity: (searchResult.lineNumber === item.lineNumber) ? item.quantity : searchResult.quantity,
                price: (searchResult.lineNumber === item.lineNumber) ? item.price : searchResult.price,
                discount: (searchResult.lineNumber === item.lineNumber) ? item.discount : searchResult.discount,
            };
        });
        setSearchResults(newSearchResults);
    };

    const handleAddSelectedLines = () => {
        // not terribly wild about this, but to be
        onChange([
            ...value,
            ...(searchResults
                    // @ts-ignore
                    .filter((result: any) => selectedLines.hasOwnProperty(result.lineNumber) && selectedLines[result.lineNumber] === true)
                    .map((line: any) => ({
                        input_39: line.lineNumber,
                        input_24: line.quantity,
                        input_37: {text: line.catalogNumber, label: line.catalogNumber, value: line.catalogNumber},
                        input_38: line.price,
                        // @ts-ignore
                        input_34: line.price * (line.quantity / UOM_MAP[line.unitOfMeasure]) * line.discount,
                        input_41: {
                            text: line.unitOfMeasure,
                            label: line.unitOfMeasure,
                            // @ts-ignore
                            value: UOM_MAP[line.unitOfMeasure]
                        },
                        input_42: line.discount
                    }))
            )
        ], {
            input_26: currentPurchaseOrderNumber,
            input_73: currentInvoiceNumber,
            input_30: currentOrderNumber,
        });
        handleNewSearch();
        setShowModal(false);
    };

    const handleChangeGlobalDiscount = () => {
        value && onChange && onChange([
            ...(value.map((line: any) => ({
                ...line,
                // @ts-ignore
                input_34: line.input_38 * (line.input_24 / line.input_41.value) * globalDiscount,
                input_42: globalDiscount
            })))
        ]);
        setGlobalDiscount('');
    };

    return (
        <div>
            <NestedForm
                {...rest}
                value={value}
                onChange={onChange}
                additionalButtons={[
                    <button key="invoice-search-button"
                            className="btn btn-secondary ml-2"
                            onClick={() => {
                                setShowModal(true)
                            }}
                    >
                        Search by Invoice / Order
                    </button>,
                    <div key="global-discount"
                         className="pt-1 flex-grow-1 text-right">
                        <input className="form-control form-control-sm"
                               placeholder="Discount"
                               value={globalDiscount}
                               onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                   setGlobalDiscount(e.target.value);
                               }}
                               style={{
                                   width: '75px',
                                   display: 'inline-block'
                               }}
                        />
                        <button type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={handleChangeGlobalDiscount}
                        >Apply
                        </button>
                        <small className="text-muted text-right"
                               style={{
                                   display: 'block'
                               }}
                        >Apply discount to all lines</small>
                    </div>
                ]}
            />
            {showModal && <Modal size="lg"
                                 isScrollable={true}
            >
                <div className="modal-header">
                    <h5 className="modal-title">Invoice Lines Lookup</h5>
                    <button type="button"
                            className="close"
                            tabIndex={-1}
                            onClick={() => {
                                setShowModal(false);
                            }}
                    >×
                    </button>
                </div>
                <div className="modal-body">
                    {errorMessage && <p className="alert alert-danger">
                        {errorMessage}
                    </p>}

                    {!isWorking && !searchType && <SearchForm onSearch={handleSearch}/>}
                    {isWorking && <div className="text-center"><ActivitySpinner className="ActivitySpinner"/></div>}

                    {!isWorking && searchType === SEARCH_TYPE_ORDER &&
                    <InvoicesResultList
                        results={searchResults}
                        onSelect={handleInvoiceSelect}
                        newSearch={handleNewSearch}
                    />}

                    {!isWorking && searchType === SEARCH_TYPE_INVOICE &&
                    <LinesResultList
                        key={'s-' + Object.keys(selectedLines).length}
                        results={searchResults}
                        selectedLines={selectedLines}
                        onSelect={handleSelectedLines}
                        newSearch={handleNewSearch}
                        onChange={handleLineChange}
                    />}
                </div>
                <div className="modal-footer">
                    {!isWorking && Object.keys(selectedLines).length > 0 &&
                    <button type="button"
                            className="btn btn-primary"
                            onClick={handleAddSelectedLines}
                    >Add Selected Lines</button>}
                </div>
            </Modal>}
        </div>
    );
};